import { sectionRenderer } from '@theme/section-renderer';
import { FilterUpdateEvent } from '@theme/events';

class BlogTabbed extends HTMLElement {
  /** @type {HTMLElement | null} */
  #nav = null;

  /** @type {MediaQueryList | null} */
  #mobileMediaQuery = null;

  /** @param {MediaQueryListEvent} event */
  #onMobileMediaQueryChange = (event) => {
    this.#syncNavDropdownState(event.matches);
  };

  connectedCallback() {
    this.#nav = this.querySelector('[data-blog-tabbed-nav]');
    this.#nav?.addEventListener('click', this.#onNavClick);
    this.#nav?.addEventListener('keydown', this.#onNavKeydown);
    this.#nav?.addEventListener('toggle', this.#onNavToggle, true);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeydown);
    window.addEventListener('popstate', this.#onPopState);
    this.#mobileMediaQuery = window.matchMedia('(max-width: 749px)');
    this.#mobileMediaQuery.addEventListener('change', this.#onMobileMediaQueryChange);
    this.#syncNavDropdownState(this.#isMobileNav());
    this.#scrollActiveTabIntoView();
  }

  disconnectedCallback() {
    this.#nav?.removeEventListener('click', this.#onNavClick);
    this.#nav?.removeEventListener('keydown', this.#onNavKeydown);
    this.#nav?.removeEventListener('toggle', this.#onNavToggle, true);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    window.removeEventListener('popstate', this.#onPopState);
    this.#mobileMediaQuery?.removeEventListener('change', this.#onMobileMediaQueryChange);
  }

  get #sectionId() {
    return this.getAttribute('section-id') ?? '';
  }

  #scrollActiveTabIntoView() {
    const active = this.#nav?.querySelector('[aria-current="page"]');
    if (!(active instanceof HTMLElement)) return;

    const more = active.closest('[data-blog-tabbed-more]');
    if (more instanceof HTMLDetailsElement && !active.matches('summary')) {
      more.open = true;
    }

    if (this.#isMobileNav()) return;

    active.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }

  #isMobileNav() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /** @param {boolean} [isMobile] */
  #syncNavDropdownState(isMobile = this.#isMobileNav()) {
    const mobileNav = this.querySelector('[data-blog-tabbed-mobile-nav]');
    if (!(mobileNav instanceof HTMLDetailsElement)) return;

    if (isMobile) {
      mobileNav.open = false;
      return;
    }

    mobileNav.open = true;
  }

  #closeNavPanels() {
    this.querySelectorAll('[data-blog-tabbed-more][open]').forEach((details) => {
      if (details instanceof HTMLDetailsElement) {
        details.open = false;
      }
    });

    if (this.#isMobileNav()) {
      const mobileNav = this.querySelector('[data-blog-tabbed-mobile-nav]');
      if (mobileNav instanceof HTMLDetailsElement) {
        mobileNav.open = false;
      }
    }

    this.#syncNavDropdownState();
  }

  /** @param {Event} event */
  #onNavToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    if (details.matches('[data-blog-tabbed-mobile-nav]')) {
      this.querySelectorAll('[data-blog-tabbed-more][open]').forEach((openDetails) => {
        if (openDetails instanceof HTMLDetailsElement) {
          openDetails.open = false;
        }
      });
      return;
    }

    if (!details.matches('[data-blog-tabbed-more]')) return;

    this.querySelectorAll('[data-blog-tabbed-more][open]').forEach((openDetails) => {
      if (openDetails !== details && openDetails instanceof HTMLDetailsElement) {
        openDetails.open = false;
      }
    });
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;

    if (this.#isMobileNav()) {
      const mobileNav = this.querySelector('[data-blog-tabbed-mobile-nav]');
      if (
        mobileNav instanceof HTMLDetailsElement &&
        mobileNav.open &&
        !mobileNav.contains(event.target)
      ) {
        mobileNav.open = false;
      }
    }

    this.querySelectorAll('[data-blog-tabbed-more][open]').forEach((panel) => {
      if (!(panel instanceof HTMLDetailsElement)) return;
      if (panel.contains(event.target)) return;
      panel.open = false;
    });
  };

  /** @param {KeyboardEvent} event */
  #onDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;

    if (this.#isMobileNav()) {
      const openMobile = this.querySelector('[data-blog-tabbed-mobile-nav][open]');
      if (openMobile instanceof HTMLDetailsElement) {
        openMobile.open = false;
        openMobile.querySelector('summary')?.focus();
        return;
      }
    }

    const openMore = this.querySelector('[data-blog-tabbed-more][open]');
    if (!(openMore instanceof HTMLDetailsElement)) return;

    openMore.open = false;
    openMore.querySelector('summary')?.focus();
  };

  /** @param {KeyboardEvent} event */
  #onNavKeydown = (event) => {
    if (!(event.target instanceof HTMLAnchorElement)) return;
    if (!this.#nav?.contains(event.target)) return;
    const links = this.#nav.querySelectorAll('a.blog-tabbed__tab');
    if (!links.length) return;
    const list = Array.from(links);
    const index = list.indexOf(event.target);
    if (index === -1) return;

    let nextIndex = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      nextIndex = Math.min(index + 1, list.length - 1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      nextIndex = Math.max(index - 1, 0);
    } else if (event.key === 'Home') {
      event.preventDefault();
      nextIndex = 0;
    } else if (event.key === 'End') {
      event.preventDefault();
      nextIndex = list.length - 1;
    } else {
      return;
    }
    list[nextIndex]?.focus();
  };

  /** @param {MouseEvent} event */
  #onNavClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (!link || !this.#nav?.contains(link)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button !== 0) return;
    if (window.Shopify?.designMode) return;

    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    if (!url.pathname.includes('/blogs/')) return;

    event.preventDefault();
    void this.#navigate(url);
  };

  /** @param {URL} url */
  async #navigate(url) {
    const sectionId = this.#sectionId;
    if (!sectionId) return;

    try {
      await sectionRenderer.renderSection(sectionId, {
        cache: false,
        url,
      });
      history.pushState({}, '', `${url.pathname}${url.search}`);
      document.dispatchEvent(new FilterUpdateEvent(new URLSearchParams(url.search)));
      this.#closeNavPanels();
      this.#scrollActiveTabIntoView();
    } catch {
      window.location.assign(url.toString());
    }
  }

  #onPopState = () => {
    const sectionId = this.#sectionId;
    if (!sectionId) return;
    void sectionRenderer.renderSection(sectionId, {
      cache: false,
      url: new URL(window.location.href),
    });
    document.dispatchEvent(new FilterUpdateEvent(new URLSearchParams(window.location.search)));
    this.#syncNavDropdownState();
    this.#scrollActiveTabIntoView();
  };
}

if (!customElements.get('blog-tabbed')) {
  customElements.define('blog-tabbed', BlogTabbed);
}
